import React, { useState, useEffect, useRef } from 'react'

import { usePageEditorContext } from '../../contexts/page-editor.context'
import { ReportBlock } from '../../blocks/report-block'
import { selectComponentService } from '../../../common/services/select-component.service'
import { reportDataSetService } from '../report-data-set.service'
import { DatasetFieldsEditor, DatasetEmptyState } from '../dataset-fields-editor'
import { SUBGROUP_COLUMN } from '../report-data-set.constants'
import { DataFieldSelect } from '../data-field-select'
import { FORMAT_OPTION } from '../format-options.service'
import { REGIONS, REGION_LEVELS, SCALE_TYPES } from 'nswidgets/src/components/map/constants'
import { BlockEditButtonGroup } from '../block-edit-button-group'

const dataFields = [
  // key, label, isRequired
  ['regionField', 'Region Field', true],
  ['valueField', 'Value Field', true],
  ['value2Field', 'Secondary Value Field', false]
]

const subgroupColumns = [SUBGROUP_COLUMN.SLIDER, SUBGROUP_COLUMN.PAGE_FILTER, SUBGROUP_COLUMN.PRIMARY, SUBGROUP_COLUMN.FILTERS]
const mapTypeOptions = [
  { value: REGIONS.US, label: 'United States' },
  { value: REGIONS.NC, label: 'North Carolina' },
  { value: REGIONS.GLOBAL, label: 'Global' }
]
const mapLevelOptions = [
  { value: REGION_LEVELS.COUNTY, label: 'County Level' },
  { value: REGION_LEVELS.ZIP, label: 'Zip Code Level' }
]

export const MapBlockEditInline = function () {
  const pageEditorContext = usePageEditorContext() || {}
  const currentEdit = pageEditorContext.currentEdit || {}
  const block = currentEdit.block || {}
  block.props = block.props || {}
  block.props.dataDefinition = block.props.dataDefinition || {}
  const dataDefinition = block.props.dataDefinition

  const dataSetOptions = useRef(reportDataSetService.getDataSetOptions(pageEditorContext.page, 'map'))
  const [editMode, setEditMode] = useState(true)
  const [dataKeyStatus, setDataKeyStatus] = useState(getDataKeyStatus())
  const [dataFieldOptions, setDataFieldOptions] = useState(getDataFieldOptions())
  const [mapType, setMapType] = useState(block.props.region)
  const [mapLevel, setMapLevel] = useState(block.props.regionLevel)

  useEffect(() => {
    // Disable the Save button if the required data fields are not populated
    if (!areRequiredFieldsPopulated()) {
      pageEditorContext.updateCurrentEdit({ isValid: false })
    }
  }, [])

  useEffect(() => {
    const dataKeyStatus = getDataKeyStatus()

    if (dataKeyStatus.isValid) {
      setDataFieldOptions(getDataFieldOptions())
    }
    setDataKeyStatus(dataKeyStatus)
  }, [block.dataKey, block.props.parentControlBar, block.props.parentBlock])

  useEffect(() => {
    updateCurrentEdit({ block })
  }, [mapType, mapLevel])

  function updateCurrentEdit (currentEdit) {
    // Ensure any updates to the block result in a new object
    if (currentEdit.block) {
      currentEdit.block = { ...currentEdit.block }
    }

    // Enables/Disables the Save button
    currentEdit.isValid = areRequiredFieldsPopulated()

    pageEditorContext.registerUnsavedChanges()
    pageEditorContext.updateCurrentEdit(currentEdit)
  }

  function getDataKeyStatus () {
    return {
      isSelected: block.dataKey !== undefined,
      isValid: selectComponentService.isKnownField(dataSetOptions.current, block.dataKey)
    }
  }

  function areRequiredFieldsPopulated () {
    const result = !dataFields.find(([key, label, isRequired]) => {
      const value = dataDefinition[key]
      return isRequired && (value === undefined || value === null)
    })

    return result && ((mapType === REGIONS.NC && !!mapLevel) || [REGIONS.US, REGIONS.GLOBAL].includes(mapType))
  }

  function getDataFieldOptions () {
    return reportDataSetService.getDataFieldOptions(pageEditorContext.page, block.dataKey)
  }

  function handleDataSetChange ({ value }) {
    // Clear out data field values
    dataFields.forEach(([key]) => {
      handleDataFieldChange(key, undefined)
    })

    if (block.dataKey === undefined) {
      setBlockDefaults()
    }

    block.dataKey = value
    updateCurrentEdit({ block })
  }

  function setBlockDefaults () {
    delete block.props.placeholder
    block.props.region = REGIONS.US
    block.props.useLetterCodes = true
    block.props.valueLabel = 'Value'
    block.props.scaleOptions = {
      type: SCALE_TYPES.SYMLOG,
      constant: 1
    }
    block.props.valueType = FORMAT_OPTION.NONE
  }

  function handleDataFieldChange () {
    updateCurrentEdit({ block })
  }

  function handleRegionChange (key, newValue) {
    if (key === 'region') {
      block.props.region = newValue
      setMapType(newValue)

      if ([REGIONS.US, REGIONS.GLOBAL].includes(newValue)) {
        delete block.props.regionLevel
        setMapLevel()
      }
    } else {
      block.props.regionLevel = newValue
      setMapLevel(newValue)
    }
  }

  if (!dataKeyStatus.isSelected) {
    return <DatasetEmptyState block={block} dataSetOptions={dataSetOptions.current} isDatasetKeyValid={dataKeyStatus.isValid} handleDataSetChange={handleDataSetChange} />
  }

  return (
    <div className="report-editor-inline">
      <div className={editMode ? 'button-row-edit-mode' : 'button-row'}>
        {block.dataKey !== undefined && (
          <BlockEditButtonGroup isInEditMode={editMode} onModeToggle={setEditMode} canPreview={areRequiredFieldsPopulated()} />
        )}
      </div>
      {editMode
        ? (
          <DatasetFieldsEditor
            block={block}
            datasetOptions={dataSetOptions.current}
            isDatasetKeyValid={dataKeyStatus.isValid}
            handleDataSetChange={handleDataSetChange}
            dataFields={dataFields}
            dataFieldOptions={dataFieldOptions}
            handleDataFieldChange={handleDataFieldChange}
            subgroupColumns={subgroupColumns}
            showSplitChartOption
            additionalFields={<>
              <DataFieldSelect selectedValue={mapType} dataFieldOptions={mapTypeOptions} dataKey="region" label="Map Type" isRequired={true} onChange={handleRegionChange} />
              <DataFieldSelect selectedValue={mapLevel} dataFieldOptions={mapLevelOptions} dataKey="regionLevel" label="Map Level" isRequired={mapType === REGIONS.NC} onChange={handleRegionChange} isDisabled={[REGIONS.US, REGIONS.GLOBAL].includes(mapType)} />
            </>}
          />
        )
        // Preview
        : (
          <ReportBlock
            blockKey={block.key}
            report={pageEditorContext.page}
            isPreviewMode={true}
            {...block}
          />
        )
      }
    </div>
  )
}
